import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { ActionWith } from 'common/interfaces/action-with';
import { State } from 'common/interfaces/state';
import { Forms } from '../../constants';
import { AccountActions } from '../account/actions/creators';
import { PeopleActions } from './actions/actions';
import * as PeoplePayloads from './actions/payloads';
import { PeopleActionTypes } from './actions/types';
import { PeopleApi } from './api';
import { InvitationCompanyUsers } from './interfaces/invitation-company-users';
import { InvitationInfo } from './interfaces/invitation-info';
import { Person } from './interfaces/person';


function* getCompanyUsers({ payload: companyId }: ActionWith<number>): SagaIterator {
  try {
    const users: Person[] = yield call(PeopleApi.getCompanyUsers);
    yield put(PeopleActions.getCompanyPeopleSucceeded(companyId, users));
  } catch (error) {
    console.error('people: get company users failed', error, { companyId });
  }
}

function* inviteUsersToCompany(): SagaIterator {
  try {
    const state: State = yield select();
    const companyId = state.account.selectedCompany.id;
    const form: InvitationCompanyUsers = state.form[Forms.INVITE_TO_COMPANY].values;
    form.companyId = companyId;
    const users: Person[] = yield call(PeopleApi.inviteUsersToCompany, form);
    yield put(PeopleActions.getCompanyPeopleSucceeded(companyId, users));
  } catch (error) {
    console.error('people: invite users to company failed', error);
  }
}

function* invitePeopleToCompany({ payload: invites }: ActionWith<InvitationCompanyUsers[]>): SagaIterator {
  try {
    const companyId = invites[0].companyId;
    const users = new Array<Person>();
    for (const invite of invites) {
      const invitedusers: Person[] = yield call(PeopleApi.inviteUsersToCompany, invite);
      users.push(...invitedusers);
    }
    yield put(PeopleActions.getCompanyPeopleSucceeded(companyId, users));
  } catch (error) {
    console.error('people: invite people to company failed', error, invites);
  }
}


function* tryConfirmInvite(
  action: ActionWith<PeoplePayloads.InviteInfo>,
): SagaIterator {
  try {
    const data: InvitationInfo = yield call(
      PeopleApi.getInvitationInfo,
      action.payload,
    );
    if (data.registered) {
      yield call(confirmInvitation, action);
    } else {
      yield put(PeopleActions.inviteGetInfoSuccessed(data));
      // TODO: redirect unregistered invited user to sign-up
    }
  } catch (error) {
    console.error('people: try confirm invite failed', error, action.payload);
  }
}

function* confirmInvitation(
  action: ActionWith<PeoplePayloads.InviteInfo>,
): SagaIterator {
  try {
    yield call(PeopleApi.acceptInvitation, action.payload);
  } catch (error) {
    console.error('people: confirm invitation failed', error, action.payload);
  }
}

function* deleteCompanyPerson({ payload: userToDeleteId }: ActionWith<string>): SagaIterator {
  try {
    const loggedInUserId: string = yield select<State>(
      (state) => state.account.id,
    );
    yield call(PeopleApi.deleteCompanyPerson, userToDeleteId);
    if (loggedInUserId === userToDeleteId) {
      yield put(AccountActions.fetchCompanies());
    }
    yield put(PeopleActions.getCompanyPeople());
  } catch (error) {
    console.error('people: delete company person failed', error, { userToDeleteId });
  }
}


function* resendInvitation(action: ActionWith<string>): SagaIterator {
  try {
    const userId = action.payload;
    yield call(PeopleApi.resendInvitation, userId);
    const companyId: number | null = yield select<State>(s => s.account.selectedCompany.id);

    yield put(PeopleActions.resendInvitationSucceeded(companyId, userId));
  } catch (error) {
    console.error('people: resend invitation failed', error, action.payload);
  }
}

function* updateUserRole(action: ActionWith<PeoplePayloads.UpdateUserRolePayload>): SagaIterator {
  try {
    const { userId, companyId, roleId } = action.payload;
    yield call(PeopleApi.updateUserRole, userId, roleId);
    yield put(PeopleActions.updateUserRoleSucceeded(userId, companyId, roleId));
    yield put(AccountActions.fetchCompanies());
  } catch (error) {
    console.error('people: update user role failed', error, action.payload);
  }
}

export function* peopleSagas(): SagaIterator {
  yield takeLatest(PeopleActionTypes.COMPANY_PEOPLE_GET_REQUEST, getCompanyUsers);
  yield takeLatest(PeopleActionTypes.INVITE_TO_COMPANY_REQUEST, inviteUsersToCompany);
  yield takeLatest(PeopleActionTypes.INVITE_INFO_GET_REQUEST, tryConfirmInvite);
  yield takeLatest(PeopleActionTypes.INVITE_CONFIRM_REQUEST, confirmInvitation);
  yield takeEvery(PeopleActionTypes.DELETE_COMPANY_PERSON_REQUEST, deleteCompanyPerson);
  yield takeLatest(PeopleActionTypes.RESEND_INVITATION_REQUEST, resendInvitation);
  yield takeEvery(PeopleActionTypes.UPDATE_USER_ROLE_REQUEST, updateUserRole);
  yield takeLatest(PeopleActionTypes.INVITE_TO_COMPANY, invitePeopleToCompany);
}
